import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissoes } from '../hooks/usePermissoes';
import { platformIamService } from '../services/api';
import { isAdminRole, normalizeRole, routeResourceMap } from '../utils/rbacFrontend';
import { canAccessPlatformRouteWithContext } from '../utils/platformPermissions';

const platformPermissionCache = new Map();
const platformPermissionInFlight = new Map();

const toPlatformPermissionSnapshot = (payload) => ({
  permissions: Array.isArray(payload?.permissions)
    ? payload.permissions
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    : [],
  usingLegacyFallback: payload?.using_legacy_fallback === true,
});

const getPlatformPermissionCacheKey = (user) => {
  const fromId = String(user?.id || '').trim();
  if (fromId) return `id:${fromId}`;

  const fromPlatformUserId = String(user?.platform_user_id || '').trim();
  if (fromPlatformUserId) return `platform:${fromPlatformUserId}`;

  const fromEmail = String(user?.email || '').trim().toLowerCase();
  if (fromEmail) return `email:${fromEmail}`;

  return 'anonymous-platform-admin';
};

const getPlatformPermissionsCached = async (cacheKey) => {
  if (platformPermissionCache.has(cacheKey)) {
    return platformPermissionCache.get(cacheKey);
  }

  if (platformPermissionInFlight.has(cacheKey)) {
    return platformPermissionInFlight.get(cacheKey);
  }

  const request = platformIamService
    .getIamMePermissions()
    .then((response) => {
      const snapshot = toPlatformPermissionSnapshot(response?.data || {});
      platformPermissionCache.set(cacheKey, snapshot);
      return snapshot;
    })
    .finally(() => {
      platformPermissionInFlight.delete(cacheKey);
    });

  platformPermissionInFlight.set(cacheKey, request);
  return request;
};

const PrivateRoute = ({
  children,
  allowedRoles,
  strictRoles = false,
  requirePlatformAdmin = false,
  requiredPlatformRoutePermission,
}) => {
  const { loading, user } = useAuth();
  const { loading: permissaoLoading, error: permissaoError, podeVisualizar, isAdmin } = usePermissoes({
    enabled: !requirePlatformAdmin,
  });
  const location = useLocation();
  const [platformPermissionState, setPlatformPermissionState] = React.useState({
    loading: false,
    error: false,
    permissionsLoaded: false,
    usingLegacyFallback: false,
    permissions: [],
  });

  React.useEffect(() => {
    if (!requirePlatformAdmin || user?.is_platform_admin !== true) {
      setPlatformPermissionState({
        loading: false,
        error: false,
        permissionsLoaded: false,
        usingLegacyFallback: false,
        permissions: [],
      });
      return;
    }

    let cancelled = false;
    const cacheKey = getPlatformPermissionCacheKey(user);

    setPlatformPermissionState((prev) => ({
      ...prev,
      loading: true,
      error: false,
    }));

    getPlatformPermissionsCached(cacheKey)
      .then((snapshot) => {
        if (cancelled) return;
        setPlatformPermissionState({
          loading: false,
          error: false,
          permissionsLoaded: true,
          usingLegacyFallback: snapshot.usingLegacyFallback,
          permissions: snapshot.permissions,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setPlatformPermissionState({
          loading: false,
          error: true,
          permissionsLoaded: false,
          usingLegacyFallback: false,
          permissions: [],
        });
      });

    return () => {
      cancelled = true;
    };
  }, [requirePlatformAdmin, user]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requirePlatformAdmin) {
    if (user?.is_platform_admin === true) {
      const resolvedRoutePermissionPath = String(
        requiredPlatformRoutePermission || location.pathname || ''
      ).trim();
      const isPlatformPath = resolvedRoutePermissionPath.startsWith('/platform');

      if (isPlatformPath) {
        const allowed = canAccessPlatformRouteWithContext({
          pathname: resolvedRoutePermissionPath,
          userPermissions: platformPermissionState.permissions,
          isPlatformAdmin: true,
          usingLegacyFallback: platformPermissionState.usingLegacyFallback,
          permissionsLoaded: platformPermissionState.permissionsLoaded,
          hasPermissionLoadError: platformPermissionState.error,
        });

        if (!allowed) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
              <div>Acesso não autorizado</div>
            </div>
          );
        }
      }

      return children;
    }

    try {
      sessionStorage.setItem('acesso_negado_role', (user?.role || '').toString());
      sessionStorage.setItem('acesso_negado_allowed', JSON.stringify(['PLATFORM_ADMIN']));
      sessionStorage.setItem('acesso_negado_path', location.pathname || '');
    } catch {
      // ignore storage errors
    }

    return <Navigate to="/acesso-negado" replace />;
  }

  const normalizedRole = normalizeRole(user?.role || user?.perfil);
  const hasOperationalTenant = Boolean(user?.current_tenant_id);
  const recursoDaRota = routeResourceMap[location.pathname];

  if (recursoDaRota) {
    if (isAdmin) {
      if (!hasOperationalTenant) {
        return <Navigate to="/acesso-negado" replace />;
      }
      return children;
    }

    if (!hasOperationalTenant) {
      return <Navigate to="/acesso-negado" replace />;
    }

    if (permissaoLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <div>Carregando...</div>
        </div>
      );
    }

    if (permissaoError || !podeVisualizar(recursoDaRota)) {
      return <Navigate to="/acesso-negado" replace />;
    }
  }

  const allowed = (allowedRoles || []).map((role) => normalizeRole(role));
  const admiteBypassDeAdmin = !strictRoles;

  if (
    allowedRoles &&
    user &&
    !allowed.includes(normalizedRole) &&
    !(admiteBypassDeAdmin && isAdminRole(normalizedRole))
  ) {
    try {
      sessionStorage.setItem('acesso_negado_role', normalizedRole || '');
      sessionStorage.setItem('acesso_negado_allowed', JSON.stringify(allowedRoles || []));
      sessionStorage.setItem('acesso_negado_path', location.pathname || '');
    } catch {
      // ignore storage errors
    }
    return <Navigate to="/acesso-negado" replace />;
  }

  return children;
};

export default PrivateRoute;
